import React, { useMemo } from 'react';
import { Box, Container, Paper, Typography, Button, Link } from '@mui/material';
import { motion } from 'framer-motion';
import ModernFormFields from './components/ModernFormFields';
import ModernBrandingSection from './components/ModernBrandingSection';
import { useAuthLogic } from '../hooks/useAuthLogic';
import { useUniversityConfig } from '../../../contexts/UniversityConfigProvider';
import TopLogo from './components/TopLogo';
import { useThemeMode } from '../../../contexts/ThemeProvider';
import ThemeToggle from '../../../components/ThemeToggle';

const ModernTemplate: React.FC = () => {
  const { config } = useUniversityConfig();
  const { mode } = useThemeMode();
  const authLogic = useAuthLogic();

  const { loginMode, isLoading, handleSubmit, otpSent, changeLoginMode, LOGIN_MODES } = authLogic;

  const loginConfig = config?.login || {};
  const leftSectionConfig = loginConfig.leftSection || {};
  const layoutConfig = loginConfig.layout || {};
  const formConfig = loginConfig.form || {};
  const loginMethodsConfig = loginConfig.loginMethods || {};
  const primaryColor = config?.theme?.primary || '#ec027c';
  const contentOnRight = layoutConfig.contentSide === 'right';
  const isDark = mode === 'dark';

  const getBackgroundStyles = () => {
    const baseImage = leftSectionConfig.backgroundImage;

    if (isDark) {
      return {
        background: `url("${baseImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: `blur(${leftSectionConfig.backgroundBlur || 0}px) brightness(0.4) contrast(1.1) saturate(0.8)`
      };
    }

    return {
      background: `url("${baseImage}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: leftSectionConfig.backgroundBlur ? `blur(${leftSectionConfig.backgroundBlur}px)` : 'none'
    };
  };

  const getOverlayGradient = () => {
    if (isDark) {
      return 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(15, 15, 35, 0.75) 100%)';
    }
    return leftSectionConfig.overlayGradient || 'linear-gradient(135deg, rgba(0, 36, 101, 0.6) 0%, rgba(0, 36, 101, 0.8) 100%)';
  };

  const onSubmit = async (event: React.FormEvent) => {
    const result = await handleSubmit(event);

    if (result.success) {
      switch (result.type) {
        case 'login':
          console.log('Login successful:', result.data);
          break;
        case 'otp_sent':
          console.log('OTP sent successfully');
          break;
        case 'reset_sent':
          console.log('Reset email sent successfully');
          break;
      }
    } else {
      console.error('Authentication failed:', result.error);
    }
  };

  const LoginMethodSwitcher = useMemo(() => {
    if (loginMode === LOGIN_MODES.FORGOT_PASSWORD) return null;

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.1 }}>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.8)',
              mb: 1
            }}
          >
            {loginMode === LOGIN_MODES.EMAIL
              ? loginMethodsConfig.emailWithOtp?.enabled
                ? 'Prefer to use OTP instead?'
                : ''
              : 'Prefer to use password instead?'}{' '}
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => changeLoginMode(loginMode === LOGIN_MODES.EMAIL ? LOGIN_MODES.PHONE : LOGIN_MODES.EMAIL)}
            sx={{
              color: 'white',
              textDecoration: 'underline',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: { xs: '44px', md: 'auto' },
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            {loginMode === LOGIN_MODES.EMAIL ? (loginMethodsConfig.emailWithOtp?.enabled ? 'Login with Email OTP' : '') : 'Login with Email Password'}{' '}
          </Link>
        </Box>
      </motion.div>
    );
  }, [loginMode, loginMethodsConfig.emailWithOtp?.enabled, changeLoginMode, LOGIN_MODES, isDark]);

  const FormSection = useMemo(
    () => (
      <motion.div
        initial={{ opacity: 0, x: contentOnRight ? -50 : 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: contentOnRight ? 'flex-start' : 'center',
          minHeight: '100vh',
          padding: contentOnRight ? '2rem 0 2rem 7rem' : '2rem 7rem'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: formConfig.borderRadius === 'large' ? 2 : 1,
            background: isDark ? 'rgba(20, 20, 20, 0.85)' : 'rgba(255, 255, 255, 0.10)',
            backdropFilter: 'blur(15px)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
            minWidth: { xs: 320, sm: 380, md: 420 },
            maxWidth: { xs: '90vw', sm: 420 },
            width: { xs: '100%', sm: 'auto' },
            boxShadow: isDark ? '0 25px 45px rgba(0, 0, 0, 0.4)' : '0 25px 45px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            ...(contentOnRight && {
              ml: { xs: 0, lg: 20 }
            })
          }}
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
            <Typography
              variant="h5"
              fontWeight="600"
              textAlign="center"
              mb={1}
              sx={{
                color: config?.theme?.accent || 'white',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {loginMode === LOGIN_MODES.FORGOT_PASSWORD ? 'Reset Password' : loginConfig.title || 'Welcome Back'}
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.8)',
                mb: 4,
                fontSize: { xs: '0.875rem', sm: '0.875rem' }
              }}
            >
              {loginMode === LOGIN_MODES.FORGOT_PASSWORD
                ? 'Enter your email to receive reset instructions'
                : loginConfig.subtitle || 'Sign in to your account'}
            </Typography>
          </motion.div>

          <Box component="form" onSubmit={onSubmit}>
            <ModernFormFields authLogic={authLogic as any} config={config} />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: 1,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  color: 'white',
                  boxShadow: `0 8px 32px ${primaryColor}40`,
                  border: 'none',
                  minHeight: { xs: '48px', sm: '56px' },
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    background: isLoading
                      ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
                      : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                    boxShadow: isLoading ? `0 8px 32px ${primaryColor}40` : `0 12px 40px ${primaryColor}50`
                  },
                  '&:disabled': {
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                    color: 'white'
                  }
                }}
              >
                {isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '10%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                    Loading...
                  </Box>
                ) : (
                  <>
                    {loginMode === LOGIN_MODES.FORGOT_PASSWORD
                      ? 'SEND RESET INSTRUCTIONS'
                      : loginMode === LOGIN_MODES.PHONE && !otpSent
                      ? 'SEND OTP'
                      : 'SIGN IN'}
                  </>
                )}
              </Button>
            </motion.div>

            {LoginMethodSwitcher}
          </Box>
        </Paper>
      </motion.div>
    ),
    [
      contentOnRight,
      formConfig.borderRadius,
      loginMode,
      loginConfig.title,
      loginConfig.subtitle,
      config?.theme?.accent,
      onSubmit,
      authLogic,
      config,
      primaryColor,
      isLoading,
      otpSent,
      LOGIN_MODES,
      LoginMethodSwitcher,
      isDark
    ]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        fontFamily: config?.theme?.fonts?.primary || '"Inter", "Roboto", sans-serif',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          ...getBackgroundStyles()
          // zIndex: -2
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: getOverlayGradient(),
          zIndex: -1
        }
      }}
    >
      <TopLogo logo={loginConfig.logo || config?.theme?.logo} universityName={config?.name} />
      <ThemeToggle />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '100vh',
            gap: { xs: 4, lg: 8 },
            flexDirection: contentOnRight ? 'row-reverse' : 'row',
            '@media (max-width: 1200px)': {
              justifyContent: 'center'
            }
          }}
        >
          {leftSectionConfig.enabled !== false && (
            <Box
              sx={{
                flex: 1,
                display: { xs: 'none', lg: 'flex' }
              }}
            >
              <ModernBrandingSection config={config} contentOnRight={contentOnRight} />
            </Box>
          )}

          <Box
            sx={{
              '@media (min-width: 1200px)': {
                flexShrink: 0
              },
              '@media (max-width: 1199px)': {
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }
            }}
          >
            {FormSection}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ModernTemplate;
